import { FC, useContext, useEffect, useMemo, useCallback, useState } from "react";
import { useParams } from "react-router-dom";
import { observer } from "mobx-react-lite";
import { FloatLabel } from "primereact/floatlabel";
import { Calendar } from "primereact/calendar";
import "../../styles/modalWindow.css";
import { Context } from "../../..";
import "../../Task/TaskAddEditForm/TaskAddEditForm.css";
import "./AddEditProject.css";
import { AppContext } from "../../../App";
import { getFileUrlFromId } from "../../../helpers/getFileUrlFromId";
import { CurrentTab, TabNavigator } from "../../UiLib/TabNavigator/TabNavigator";
import CheckboxIos from "../../UiLib/CheckboxIos/CheckboxIos";
import { useUI } from "../../../contexts/UiContext";
import WidgetContainer from "../../UiLib/WidgetContainer/WidgetContainer";
import ConfirmationWindow from "../ConfirmationWindow/ConfirmationWindow";
import { ProjectContext } from "../../../pages/Project/Project";
import ImageSelector from "../ImageSelector/ImageSelector";
import {
    ABBREVIATION_MAX_LENGTH,
    ABBREVIATION_MIN_LENGTH,
    MAX_DESCRIPTION_LENGTH,
    MAX_NAME_LENGTH,
    PROJECT_SETTINGS_DEFAULT,
} from "../../../common/constants/ProjectConst";
import { ProjectSettings } from "../../../common/api_interfaces/project/ProjectSettings";
import { IProjectCreateReq } from "../../../common/api_interfaces/project/IProjectCreateReq";
import { IProjectEditReq } from "../../../common/api_interfaces/project/IProjectEditReq";
import Loader from "../Loader/Loader";

interface IProjectInfo {
    name: string;
    description?: string;
    startDate: Date;
    endDate: Date | null;
    photoId: number | null;
    abbreviation: string;
}

interface IEditProject {
    projectSetting?: ProjectSettings | undefined;
    setOpen: (open: boolean) => void;
    projectInfo?: IProjectInfo;
    onAction?: () => void;
    editSubproject?: boolean;
}

const AddEditProject: FC<IEditProject> = observer(({ projectSetting, setOpen, projectInfo, onAction }) => {
    const { id } = useParams();
    const { store } = useContext(Context);
    const { getData, showToast } = useContext(AppContext);
    const { handleRefreshPage } = useContext(ProjectContext) || {};
    const [showLoader, setShowLoader] = useState<boolean>(false);

    const [name, setName] = useState(projectInfo?.name || "");
    const [abbreviation, setAbbreviation] = useState(projectInfo?.abbreviation || "");
    const [abbreviationError, setAbbreviationError] = useState<string>();
    const [nameError, setNameError] = useState<string>();
    const [description, setDescription] = useState(projectInfo?.description || "");
    const [startDate, setStartDate] = useState<Date | undefined>(
        projectInfo?.startDate ? new Date(projectInfo.startDate) : undefined
    );
    const [newPhoto, setNewPhoto] = useState<File>();
    const [endDate, setEndDate] = useState<Date | null>(projectInfo?.endDate ? new Date(projectInfo.endDate) : null);
    const [datesError, setDatesError] = useState<string>();
    const [imageId, setImageId] = useState(projectInfo?.photoId ?? 1);
    const [projectSettingsSave, setProjectSettingsSave] = useState<ProjectSettings>(
        projectSetting ?? PROJECT_SETTINGS_DEFAULT
    );
    const [openConfirmStatusWindow, setOpenConfirmStatusWindow] = useState(false);

    const EDIT_MODE = useMemo(() => projectInfo !== undefined, [projectInfo]);

    useEffect(() => {
        setProjectSettingsSave(projectSetting ?? PROJECT_SETTINGS_DEFAULT);
    }, [projectSetting]);

    const uploadImageOrDefault = useCallback(async (): Promise<number> => {
        if (!newPhoto) return imageId;
        const response = await store.fileService.uploadTempFile(newPhoto);
        setImageId(response.data.id);
        setNewPhoto(undefined);
        return response.data.id;
    }, [newPhoto, imageId, store.fileService]);

    const validateName = useCallback((): boolean => {
        let nameErr: string | undefined;

        if (!name.trim()) {
            nameErr = "Название проекта не заполнено";
        } else if (name.trim().length > MAX_NAME_LENGTH) {
            nameErr = `Название проекта не может быть больше ${MAX_NAME_LENGTH} символов`;
        }

        setNameError(nameErr);
        return !!nameErr;
    }, [name]);

    const validateDates = useCallback((): boolean => {
        let datesErr: string | undefined;

        if (!startDate) {
            datesErr = "Заполните сроки проекта";
        } else if (startDate && endDate && endDate < startDate) {
            datesErr = "Дата начала не может быть больше даты окончания";
        }

        setDatesError(datesErr);
        return !!datesErr;
    }, [startDate, endDate]);

    const validateAbbreviation = useCallback((): boolean => {
        let nameErr: string | undefined;
        const regex = /^[a-zA-ZА-Яа-я0-9]*$/;

        if (!abbreviation.trim()) {
            nameErr = "Аббревиатура не заполнена";
        } else if (!regex.test(abbreviation)) {
            nameErr = "Пожалуйста, используйте только буквы (A-Z, a-z, А-Я, а-я) и цифры (0-9)";
        } else if (abbreviation.length < ABBREVIATION_MIN_LENGTH || abbreviation.length > ABBREVIATION_MAX_LENGTH) {
            nameErr = `Длина аббревиатуры должна быть от ${ABBREVIATION_MIN_LENGTH} до ${ABBREVIATION_MAX_LENGTH} символов`;
        }

        setAbbreviationError(nameErr);
        return !!nameErr;
    }, [abbreviation]);

    const changeName = useCallback((value: string): void => {
        setName(value);
        setAbbreviation(value.trim().slice(0, 3).toUpperCase());
    }, []);

    const handleDescriptionChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setDescription(e.target.value.slice(0, MAX_DESCRIPTION_LENGTH));
    }, []);

    const handleSave = useCallback(
        async (isEdit: boolean) => {
            if (validateName() || validateDates() || validateAbbreviation()) {
                setShowLoader(false);
                return;
            }

            setShowLoader(true);

            try {
                const photoId = await uploadImageOrDefault();

                const body = {
                    name: name.trim(),
                    description: description.trim(),
                    startDate: startDate?.toISOString(),
                    endDate: endDate?.toISOString(),
                    photoId,
                    abbreviation: abbreviation.toUpperCase(),
                    settings: projectSettingsSave,
                    ...(isEdit && { projectId: Number(id) }),
                };

                if (isEdit) {
                    await store.editProject(body as IProjectEditReq);
                    showToast("Изменения сохранены");
                } else {
                    await store.createProject(body as IProjectCreateReq);
                    showToast("Проект создан");
                }

                await Promise.all([store.getProjectGeneralInfo(Number(id)), store.getAllProject(true), getData()]);

                if (onAction) await onAction();
            } catch (error) {
                console.error("Error saving project:", error);
                showToast("Произошла ошибка при сохранении");
            } finally {
                setShowLoader(false);
                setOpen(false);
            }
        },
        [
            name,
            description,
            startDate,
            endDate,
            abbreviation,
            projectSettingsSave,
            id,
            store,
            getData,
            onAction,
            showToast,
            setShowLoader,
            validateName,
            validateDates,
            validateAbbreviation,
            uploadImageOrDefault,
        ]
    );

    const onCreate = useCallback(() => {
        handleSave(false);
    }, [handleSave]);

    const onEdit = useCallback(() => {
        handleSave(true);
        handleRefreshPage()
    }, [handleRefreshPage, handleSave]);

    const changProjectSettingChangingStatusFunc = useCallback(async () => {
        setShowLoader(true);
        try {

            const newVal = !projectSettingsSave.changeColumnStatus;
            await store.projectService.changProjectSettingChangingStatus({
                projectId: Number(id),
                changeColumnSetting: newVal,
            });

            setProjectSettingsSave((prev) => ({
                ...prev,
                ["changeColumnStatus"]: newVal,
            }));

            // if (EDIT_MODE) {
            //     handleRefreshPage();
            // }
        } catch (error) {
            console.error("Error changing project settings:", error);
        } finally {
            setShowLoader(false);
            setOpenConfirmStatusWindow(false);
        }
    }, [setShowLoader, id, projectSettingsSave.changeColumnStatus, store.projectService]);

    const changeProjectSettings = useCallback(
        <T extends ProjectSettings>(changeType: keyof T, newValue: T[keyof T]) => {
            if (EDIT_MODE && changeType === "changeColumnStatus") {
                setOpenConfirmStatusWindow(true);
                return;
            }
            setProjectSettingsSave((prev) => ({
                ...prev,
                [changeType]: newValue,
            }));
        },
        [EDIT_MODE]
    );

    const SettingsSwitch = useCallback((label: string, checked: boolean, onChange: () => void) => {
        return (
            <div className="additional-settings-switch-container">
                <label className="input_label">{label}</label>
                <CheckboxIos callBack={onChange} checked={checked} />
            </div>
        );
    }, []);

    const currentImageUrl = useMemo(
        () => (newPhoto ? URL.createObjectURL(newPhoto) : getFileUrlFromId(imageId)),
        [newPhoto, imageId]
    );

    const descriptionLength = useMemo(() => description.length, [description]);

    return (
        <div className="add-edit-project-overlay">
            {openConfirmStatusWindow && (
                <ConfirmationWindow
                    headerText="Изменение статуса колонок"
                    bodyText={
                        projectSetting?.changeColumnStatus
                            ? "Внимание! После отключения этой настройки колонки и задачи на канбан-досках будут возвращены к стандартным статусам. Пожалуйста, дождитесь завершения изменений."
                            : "Внимание! После включения этой настройки статусы колонок будут управляться вручную со страницы доски."
                    }
                    confirmText="Начать"
                    cancelText="Отмена"
                    onConfirm={changProjectSettingChangingStatusFunc}
                    onCancel={() => setOpenConfirmStatusWindow(false)}
                />
            )}
            {showLoader && <Loader />}

            <div className="add-edit-project-component">
                <WidgetContainer className="add-edit-project-container">
                    <div className="add-edit-project-column">
                        <div className="add-edit-project-tabs">
                            <TabNavigator>
                                <CurrentTab
                                    name="Основное"
                                    className={nameError || abbreviationError ? "main-tab" : ""}
                                >
                                    <div className="add-edit-project-bloks">
                                        <div>
                                            <label className="input_label label_24" htmlFor="name_input">
                                                Обложка
                                            </label>
                                            <div className="add-edit-project-bloks-covers">
                                                <div className="add-edit-project-bloks-cover_wrapper">
                                                    <div className="cover_project_main_item">
                                                        <img src={currentImageUrl} alt="Обложка проекта" />
                                                    </div>
                                                    <div className="add-edit-project-image-selector">
                                                        <ImageSelector onImageCropped={setNewPhoto} />
                                                    </div>
                                                </div>
                                                <div className="cover_project_default_images">
                                                    {[1, 2, 3].map((id) => (
                                                        <div
                                                            key={id}
                                                            className="cover_project_default_image"
                                                            onClick={() => setImageId(id)}
                                                        >
                                                            <img alt={`projectCover${id}`} src={getFileUrlFromId(id)} />
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>

                                        <label className="input_label label_24" htmlFor="name_input">
                                            Название проекта*
                                        </label>
                                        <input
                                            id="name_input"
                                            className={`input_form label_24 ${nameError ? "textarea_with_error" : ""}`}
                                            onChange={(e) => changeName(e.target.value)}
                                            onBlur={validateName}
                                            type="text"
                                            placeholder="Введите название проекта"
                                            value={name}
                                        />
                                        {nameError && <p className="error_message">{nameError}</p>}

                                        <label className="input_label label_24" htmlFor="abbreviation_input">
                                            Аббревиатура проекта*
                                        </label>
                                        <input
                                            id="abbreviation_input"
                                            className={`input_form label_24 ${
                                                abbreviationError ? "textarea_with_error" : ""
                                            }`}
                                            onChange={(e) => setAbbreviation(e.target.value.toUpperCase())}
                                            onBlur={validateAbbreviation}
                                            type="text"
                                            placeholder="Введите аббревиатуру проекта"
                                            value={abbreviation}
                                        />
                                        {abbreviationError && <p className="error_message">{abbreviationError}</p>}

                                        <label className="input_label label_24" htmlFor="desk_input">
                                            Описание проекта
                                        </label>
                                        <textarea
                                            className="input_form textarea_input label_24"
                                            value={description}
                                            placeholder="Опишите свой проект"
                                            id="desk_input"
                                            onChange={handleDescriptionChange}
                                        />
                                        <div className="task__create--edit__form--tab__panel--textarea__lenght">
                                            <div>
                                                {descriptionLength}/{MAX_DESCRIPTION_LENGTH}
                                            </div>
                                        </div>
                                    </div>
                                </CurrentTab>

                                <CurrentTab name="Сроки проекта" className={datesError ? "calendar-tab" : ""}>
                                    <div style={{ paddingTop: "10px" }}>
                                        <div
                                            className={`calendar--block__flex ${datesError ? "input_with_error" : ""}`}
                                        >
                                            <div className="calendar--block__flex--inp" style={{margin: "0px 10px 0px 0px"}}>
                                                <FloatLabel>
                                                    <Calendar
                                                        dateFormat="dd.mm.yy"
                                                        onChange={(e) => setStartDate(e.value as Date)}
                                                        value={startDate}
                                                    />
                                                    <label>Дата начала*</label>
                                                </FloatLabel>
                                            </div>
                                            <div className="calendar--block__flex--inp" style={{margin: "0px"}}>
                                                <FloatLabel>
                                                    <Calendar
                                                        dateFormat="dd.mm.yy"
                                                        onChange={(e) => setEndDate(e.value as Date)}
                                                        value={endDate}
                                                    />
                                                    <label>Дата окончания</label>
                                                </FloatLabel>
                                            </div>
                                        </div>
                                        {datesError && <p className="error_message" style={{margin: "0px"}}>{datesError}</p>}
                                    </div>
                                </CurrentTab>

                                <CurrentTab name="Настройки">
                                    <div>
                                        {SettingsSwitch(
                                            "Исполнитель задачи обязателен",
                                            projectSettingsSave.responsibleRequired ?? true,
                                            () =>
                                                changeProjectSettings(
                                                    "responsibleRequired",
                                                    !projectSettingsSave.responsibleRequired
                                                )
                                        )}
                                        {SettingsSwitch(
                                            "Разрешить изменение статуса колонок",
                                            projectSettingsSave.changeColumnStatus ?? false,
                                            () =>
                                                changeProjectSettings(
                                                    "changeColumnStatus",
                                                    !projectSettingsSave.changeColumnStatus
                                                )
                                        )}
                                    </div>
                                </CurrentTab>
                            </TabNavigator>
                        </div>

                        <div className="modal-window-btns">
                            <button
                                data-testid="button-project_edit_form-save"
                                className="modal-window-margin-btn p_white p_14 button_reset_style save_btn"
                                onClick={EDIT_MODE ? onEdit : onCreate}
                            >
                                Сохранить
                            </button>
                            <button
                                data-testid="button-project_edit_form-cancel"
                                className="confirm_btn p_blue p_14 button_reset_style"
                                onClick={() => setOpen(false)}
                            >
                                Отмена
                            </button>
                        </div>
                    </div>
                </WidgetContainer>
            </div>
        </div>
    );
});

export default AddEditProject;
